// Workflow definitions + run history. A WORKFLOW is the agentic
// counterpart to a kernel: where a kernel is a deterministic recipe
// that re-renders fresh on every view, a workflow is a saved,
// parameterised, goal-directed agent task whose every execution is
// RECORDED as a run — so you can replay it and compare the result
// over time. The run history is the feature.
//
// Persistence is IndexedDB (own DB, separate from the chat DB so we
// don't collide with the chat-surface refactor). Two object stores:
//
//   workflows  keyed by `${tenantId}:${userId}:${id}` - the saved definition.
//              `byTenant` indexes the canonical tenant-and-user owner.
//
//   runs       keyed by `${tenantId}:${userId}:${runId}` - one execution record
//              (params, scope, status, full step timeline, deliverable
//              snapshot). Indexed `byTenant` and `byWorkflow`
//              (compound [tenantId, workflowId]) so a workflow's run
//              history loads with one ranged query.
//
// Everything fails SOFT, exactly like chatStorage: if IndexedDB is
// unavailable (private browsing, quota, node test env) the wrapper
// resolves a benign empty/null/false and logs once. Callers never
// branch on storage availability.
//
// v1 scope (deliberate): client-only, per-browser, per authenticated user and tenant. Server-
// side persistence + cross-user sharing is a later branch — see the
// design notes; the run record is shaped to survive that move.

import { currentBrowserStorageOwner } from "./browserStorageScope.js";

// IDB stores follow the `origin-*` naming convention. The chat DB
// (chatStorage.js) was renamed to match in the codename cleanup; existing
// chat history isn't migrated to the new name, so it resets.
const DB_NAME = "origin-workflows";
const SCOPED_DB_PREFIX = "origin-workflows-v2";
const DB_VERSION = 1;

const _dbPromises = new Map();
let _unavailable = false;
let _legacyDiscardStarted = false;

function tenantKey(tenantId) {
    return currentBrowserStorageOwner(tenantId)?.key ?? null;
}

function compositeKey(tenantId, id) {
    return compositeKeyForOwner(tenantKey(tenantId), id);
}

function compositeKeyForOwner(ownerKey, id) {
    return `${ownerKey}:${String(id)}`;
}

function getDb(tenantId) {
    if (_unavailable) return Promise.resolve(null);
    const owner = currentBrowserStorageOwner(tenantId);
    if (!owner) return Promise.resolve(null);
    if (_dbPromises.has(owner.key)) return _dbPromises.get(owner.key);
    if (typeof indexedDB === "undefined") {
        _unavailable = true;
        // node/test env or a privacy mode with IDB disabled. Persistence
        // is best-effort; surface once and move on.
        console.warn("[workflowStore] indexedDB unavailable; workflow persistence disabled.");
        return Promise.resolve(null);
    }
    if (!_legacyDiscardStarted) {
        _legacyDiscardStarted = true;
        try { indexedDB.deleteDatabase(DB_NAME); } catch { /* unavailable */ }
    }
    const dbPromise = new Promise((resolve) => {
        let opened = false;
        const req = indexedDB.open(`${SCOPED_DB_PREFIX}:${owner.key}`, DB_VERSION);
        req.onupgradeneeded = () => {
            const db = req.result;
            if (!db.objectStoreNames.contains("workflows")) {
                const wf = db.createObjectStore("workflows", { keyPath: "key" });
                wf.createIndex("byTenant", "tenantId", { unique: false });
            }
            if (!db.objectStoreNames.contains("runs")) {
                const runs = db.createObjectStore("runs", { keyPath: "key" });
                runs.createIndex("byTenant", "tenantId", { unique: false });
                runs.createIndex("byWorkflow", ["tenantId", "workflowId"], { unique: false });
            }
        };
        req.onsuccess = () => { opened = true; resolve(req.result); };
        req.onerror = () => {
            if (opened) return;
            console.warn("[workflowStore] failed to open DB:", req.error);
            _unavailable = true;
            resolve(null);
        };
        req.onblocked = () => {
            console.warn("[workflowStore] DB open blocked by another connection.");
        };
    });
    _dbPromises.set(owner.key, dbPromise);
    return dbPromise;
}

async function getDbContext(tenantId) {
    const owner = currentBrowserStorageOwner(tenantId);
    if (!owner) return null;
    const db = await getDb(owner.tenantId);
    return db ? { db, owner } : null;
}

// ── reactivity ───────────────────────────────────────────────────
// IDB is async, so (unlike the localStorage kernel store) subscribers
// re-load on notify rather than reading synchronously. notify() fires
// after any successful write.
const listeners = new Set();
function notify() {
    for (const fn of listeners) {
        try { fn(); } catch { /* a listener throwing mustn't block siblings */ }
    }
}
export function subscribeWorkflows(fn) {
    listeners.add(fn);
    return () => listeners.delete(fn);
}

// ── definitions ──────────────────────────────────────────────────

// Normalise + validate a definition into the stored record shape.
// Exported for unit testing without a live IDB. Throws on the few
// hard requirements; everything else gets a sane default.
export function buildWorkflowRecord(tenantId, def) {
    if (!def || typeof def !== "object") throw new Error("invalid workflow");
    const command = sanitizeWorkflowCommand(def.command);
    if (!command) throw new Error("workflow needs a command name");
    const goal = typeof def.goal === "string" ? def.goal.trim() : "";
    if (!goal) throw new Error("workflow needs a `goal` (its fixed end state)");
    if (typeof def.promptTemplate !== "string" || !def.promptTemplate.trim()) {
        throw new Error("workflow needs a `promptTemplate`");
    }
    const id = def.id || newId("wf");
    return {
        key: compositeKey(tenantId, id),
        tenantId: tenantKey(tenantId),
        id,
        command,
        title: (def.title || "").trim() || command,
        description: (def.description || "").trim() || undefined,
        goal,
        promptTemplate: def.promptTemplate,
        params: normaliseParams(def.params),
        capabilities: normaliseCapabilities(def.capabilities),
        deliverable: normaliseDeliverable(def.deliverable),
        scopeMode: def.scopeMode === "scoped" ? "scoped" : "global",
        // v1 invariant: workflows are read-only. The runner enforces it
        // (read-only toolset + prompt); the flag records the intent and
        // is the seam where an "acting" workflow would later opt out.
        readOnly: true,
        createdAt: def.createdAt || Date.now(),
        updatedAt: Date.now(),
    };
}

export async function saveWorkflow(tenantId, def) {
    const record = buildWorkflowRecord(tenantId, def);
    const db = await getDb(tenantId);
    if (!db) return stripStorage(record); // fail-soft: hand back the would-be record
    await put(db, "workflows", record);
    notify();
    return stripStorage(record);
}

export async function loadWorkflows(tenantId) {
    const context = await getDbContext(tenantId);
    if (!context) return [];
    const rows = await getAllByIndex(context.db, "workflows", "byTenant", context.owner.key);
    return rows
        .map(stripStorage)
        .sort((a, b) => a.command.localeCompare(b.command));
}

export async function loadWorkflow(tenantId, id) {
    const context = await getDbContext(tenantId);
    if (!context) return null;
    const row = await get(
        context.db,
        "workflows",
        compositeKeyForOwner(context.owner.key, id),
    );
    return row ? stripStorage(row) : null;
}

// Create a blank workflow with a unique command and open-able defaults.
// Mirrors createBlankKernel — gives the Library "+ new" affordance a
// valid starting point the user can edit + run immediately.
export async function createBlankWorkflow(tenantId) {
    const existing = await loadWorkflows(tenantId);
    const used = new Set(existing.map((w) => w.command));
    let n = 1;
    let command = "new-workflow-1";
    while (used.has(command)) { n += 1; command = `new-workflow-${n}`; }
    return saveWorkflow(tenantId, {
        command,
        title: "Untitled workflow",
        goal: "Describe the fixed end state this run should reach.",
        promptTemplate: "Step-by-step instructions for the agent. Use {{param}} placeholders for the knobs.",
        params: [],
        capabilities: { analytics: true, python: true, mcpServers: [], kernels: [] },
        deliverable: { kind: "render" },
        scopeMode: "global",
    });
}

export async function deleteWorkflow(tenantId, id) {
    const context = await getDbContext(tenantId);
    if (!context) return false;
    const { db, owner } = context;
    // Drop the definition and all of its runs in one transaction so a
    // failure leaves no orphaned run history.
    const ok = await new Promise((resolve) => {
        try {
            const tx = db.transaction(["workflows", "runs"], "readwrite");
            tx.objectStore("workflows").delete(compositeKeyForOwner(owner.key, id));
            const idx = tx.objectStore("runs").index("byWorkflow");
            const cursorReq = idx.openCursor(IDBKeyRange.only([owner.key, id]));
            cursorReq.onsuccess = () => {
                const cursor = cursorReq.result;
                if (cursor) { cursor.delete(); cursor.continue(); }
            };
            tx.oncomplete = () => resolve(true);
            tx.onerror = () => { console.warn("[workflowStore] deleteWorkflow failed:", tx.error); resolve(false); };
            tx.onabort = () => { console.warn("[workflowStore] deleteWorkflow aborted:", tx.error); resolve(false); };
        } catch (err) {
            console.warn("[workflowStore] deleteWorkflow threw:", err);
            resolve(false);
        }
    });
    if (ok) notify();
    return ok;
}

// Drop every workflow definition AND every run for one tenant in a
// single transaction. Cursor-walks the `byTenant` index on both
// stores so other tenants on the same browser are untouched. Used by
// the "Clear workflows" flows in LogoutDialog and SettingsPage.
export async function deleteAllWorkflowsForTenant(tenantId) {
    const context = await getDbContext(tenantId);
    if (!context) return false;
    const { db, owner } = context;
    const tk = owner.key;
    const ok = await new Promise((resolve) => {
        try {
            const tx = db.transaction(["workflows", "runs"], "readwrite");
            for (const storeName of ["workflows", "runs"]) {
                const idx = tx.objectStore(storeName).index("byTenant");
                const cursorReq = idx.openCursor(IDBKeyRange.only(tk));
                cursorReq.onsuccess = () => {
                    const cursor = cursorReq.result;
                    if (!cursor) return;
                    cursor.delete();
                    cursor.continue();
                };
            }
            tx.oncomplete = () => resolve(true);
            tx.onerror = () => {
                console.warn("[workflowStore] deleteAllWorkflowsForTenant failed:", tx.error);
                resolve(false);
            };
            tx.onabort = () => {
                console.warn("[workflowStore] deleteAllWorkflowsForTenant aborted:", tx.error);
                resolve(false);
            };
        } catch (err) {
            console.warn("[workflowStore] deleteAllWorkflowsForTenant threw:", err);
            resolve(false);
        }
    });
    if (ok) notify();
    return ok;
}

// ── runs ─────────────────────────────────────────────────────────

// Create-or-update a run record. The runner calls this once at start
// (status "running") and again at completion (terminal status +
// timeline + deliverable). Same key, so the second write replaces the
// first — there's only ever one row per run.
export async function putRun(tenantId, run) {
    if (!run || !run.id || !run.workflowId) throw new Error("run needs id + workflowId");
    const record = {
        ...run,
        key: compositeKey(tenantId, run.id),
        tenantId: tenantKey(tenantId),
    };
    const db = await getDb(tenantId);
    if (!db) return stripStorage(record);
    await put(db, "runs", record);
    notify();
    return stripStorage(record);
}

// Run history for one workflow, newest-first. Bounded per workflow, so
// scope filtering (if the caller wants only runs bound to a given
// scope) is cheap to do in memory on the returned set.
export async function loadRuns(tenantId, workflowId) {
    const context = await getDbContext(tenantId);
    if (!context) return [];
    const rows = await getAllByIndex(
        context.db,
        "runs",
        "byWorkflow",
        [context.owner.key, workflowId],
        /* compound */ true,
    );
    return rows
        .map(stripStorage)
        .sort((a, b) => (b.startedAt || 0) - (a.startedAt || 0));
}

export async function loadRun(tenantId, runId) {
    const context = await getDbContext(tenantId);
    if (!context) return null;
    const row = await get(
        context.db,
        "runs",
        compositeKeyForOwner(context.owner.key, runId),
    );
    return row ? stripStorage(row) : null;
}

export async function deleteRun(tenantId, runId) {
    const context = await getDbContext(tenantId);
    if (!context) return false;
    const ok = await del(
        context.db,
        "runs",
        compositeKeyForOwner(context.owner.key, runId),
    );
    if (ok) notify();
    return ok;
}

// ── small IDB primitives (all fail-soft) ─────────────────────────

function put(db, store, value) {
    return new Promise((resolve) => {
        try {
            const tx = db.transaction(store, "readwrite");
            tx.objectStore(store).put(value);
            tx.oncomplete = () => resolve(true);
            tx.onerror = () => { console.warn(`[workflowStore] put ${store} failed:`, tx.error); resolve(false); };
            tx.onabort = () => { console.warn(`[workflowStore] put ${store} aborted:`, tx.error); resolve(false); };
        } catch (err) {
            console.warn(`[workflowStore] put ${store} threw:`, err);
            resolve(false);
        }
    });
}

function get(db, store, key) {
    return new Promise((resolve) => {
        try {
            const tx = db.transaction(store, "readonly");
            const req = tx.objectStore(store).get(key);
            req.onsuccess = () => resolve(req.result || null);
            req.onerror = () => { console.warn(`[workflowStore] get ${store} failed:`, req.error); resolve(null); };
        } catch (err) {
            console.warn(`[workflowStore] get ${store} threw:`, err);
            resolve(null);
        }
    });
}

function del(db, store, key) {
    return new Promise((resolve) => {
        try {
            const tx = db.transaction(store, "readwrite");
            tx.objectStore(store).delete(key);
            tx.oncomplete = () => resolve(true);
            tx.onerror = () => { console.warn(`[workflowStore] delete ${store} failed:`, tx.error); resolve(false); };
        } catch (err) {
            console.warn(`[workflowStore] delete ${store} threw:`, err);
            resolve(false);
        }
    });
}

function getAllByIndex(db, store, indexName, value, compound = false) {
    return new Promise((resolve) => {
        try {
            const tx = db.transaction(store, "readonly");
            const idx = tx.objectStore(store).index(indexName);
            const req = idx.getAll(compound ? IDBKeyRange.only(value) : IDBKeyRange.only(value));
            req.onsuccess = () => resolve(Array.isArray(req.result) ? req.result : []);
            req.onerror = () => { console.warn(`[workflowStore] getAll ${store}.${indexName} failed:`, req.error); resolve([]); };
        } catch (err) {
            console.warn(`[workflowStore] getAll ${store}.${indexName} threw:`, err);
            resolve([]);
        }
    });
}

// ── shaping helpers ──────────────────────────────────────────────

// Drop storage-only fields (`key`) before handing a record to callers
// — they work with `id`, not the tenant-composite primary key.
function stripStorage(record) {
    if (!record) return record;
    const { key: _key, ...rest } = record;
    return rest;
}

export function sanitizeWorkflowCommand(raw) {
    if (!raw) return "";
    return String(raw)
        .trim()
        .replace(/^\//, "")
        .toLowerCase()
        .replace(/[^a-z0-9-_]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 64);
}

// params: array of { name, type, default, description, required }.
// Tolerant of the object-map shape kernels use, too.
function normaliseParams(params) {
    if (Array.isArray(params)) {
        return params
            .filter((p) => p && typeof p.name === "string" && p.name.trim())
            .map((p) => ({
                name: p.name.trim(),
                type: ["number", "boolean", "string"].includes(p.type) ? p.type : "string",
                default: p.default,
                description: typeof p.description === "string" ? p.description : undefined,
                required: p.required === true,
            }));
    }
    if (params && typeof params === "object") {
        return Object.entries(params).map(([name, decl]) => ({
            name,
            type: ["number", "boolean", "string"].includes(decl?.type) ? decl.type : "string",
            default: decl?.default,
            description: typeof decl?.description === "string" ? decl.description : undefined,
            required: decl?.required === true,
        }));
    }
    return [];
}

// capabilities: which read-only tool families this workflow's agent
// may use. mcpServers + kernels are allow-lists (names only).
function normaliseCapabilities(cap) {
    const c = cap && typeof cap === "object" ? cap : {};
    return {
        analytics: c.analytics !== false, // default on — analytics is the point
        python: c.python !== false,       // default on
        mcpServers: Array.isArray(c.mcpServers) ? c.mcpServers.filter((s) => typeof s === "string") : [],
        kernels: Array.isArray(c.kernels) ? c.kernels.filter((s) => typeof s === "string") : [],
    };
}

function normaliseDeliverable(d) {
    const kind = d && d.kind === "structured" ? "structured" : "render";
    const out = { kind };
    if (kind === "structured" && d?.schema && typeof d.schema === "object") {
        out.schema = d.schema;
    }
    return out;
}

function newId(prefix) {
    try {
        if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
            return `${prefix}_${crypto.randomUUID()}`;
        }
    } catch { /* fall through */ }
    return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}
